
import React from "react";
import { ArrowLeft, Clock, Eye, Shield, Ban, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatTime } from "@/utils/formatUtils";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

interface TestHeaderProps {
  testTitle: string;
  questionCount: number;
  timeLimit: number;
  timeRemaining: number;
  isSubmitting: boolean;
  onSubmitTest: () => void;
  violations?: number;
  isLocked?: boolean;
  hasMathQuestions?: boolean;
}

const TestHeader: React.FC<TestHeaderProps> = ({
  testTitle,
  questionCount,
  timeLimit,
  timeRemaining,
  isSubmitting,
  onSubmitTest,
  violations = 0,
  isLocked = false,
  hasMathQuestions = false,
}) => {
  const navigate = useNavigate();
  
  const handleBackClick = () => {
    navigate("/mock-test-generator");
  };
  
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mb-6 bg-white rounded-lg p-4 shadow">
      <div className="flex flex-col items-start mb-4 sm:mb-0">
        <div className="flex items-center mb-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="p-0 mr-1" 
            onClick={handleBackClick}
            aria-label="Back to test generator"
          >
            <ArrowLeft className="h-4 w-4 text-gray-500" />
          </Button>
          <h1 className="text-xl font-bold text-gray-800">{testTitle}</h1>
        </div>
        <div className="text-sm text-gray-600">
          {questionCount} Questions • {timeLimit} minutes
          {hasMathQuestions && (
            <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
              <Calculator className="h-3 w-3 mr-1" />
              Math Questions
            </Badge>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {violations > 0 && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 px-2 py-1 flex items-center">
            <Shield className="h-3.5 w-3.5 mr-1" />
            <span>Violations: {violations}</span>
          </Badge>
        )}
        
        {isLocked ? (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 px-2 py-1 flex items-center">
            <Ban className="h-3.5 w-3.5 mr-1" />
            <span>Test Locked</span>
          </Badge>
        ) : (
          <div className="flex items-center bg-red-50 px-3 py-1.5 rounded-md">
            <Clock className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-red-600 font-medium">
              {formatTime(timeRemaining)}
            </span>
          </div>
        )}

        <Button
          variant="destructive"
          onClick={onSubmitTest}
          disabled={isSubmitting || isLocked}
          className="ml-2"
        >
          {isSubmitting ? "Submitting..." : "Submit Test"}
        </Button>
      </div>
    </div>
  );
};

export default TestHeader;
